import { ApeironBattleDemoTitle } from '@apeiron/ui'
import { styled, useMediaQuery } from '@mui/material'
import backgroundJPG from '@public/images/background-battle-demo.jpg'
import bottomLeftPNG from '@public/images/image-battle-demo-left-bottom.png'
import topLeftPNG from '@public/images/image-battle-demo-left-top.png'
import mobileBottomLeftPNG from '@public/images/image-battle-demo-mobile-left-bottom.png'
import mobileTopLeftPNG from '@public/images/image-battle-demo-mobile-left-top.png'
import mobileBottomRightPNG from '@public/images/image-battle-demo-mobile-right-bottom.png'
import mobileTopRightPNG from '@public/images/image-battle-demo-mobile-right-top.png'
import bottomRightPNG from '@public/images/image-battle-demo-right-bottom.png'
import topRightPNG from '@public/images/image-battle-demo-right-top.png'
import BattlepassContainer from '@src/components/battle/demo/pass/BattlepassContainer'
import BattleDemoSecNavBar from '@src/components/navigationBar/secondary/BattleDemoSecNavBar'
import ApeironCorneredBox from '@src/components/share/apeiron/ApeironCorneredBox'
import { ROUTE } from '@src/constants/route'
import { BattlepassProvider } from '@src/contexts/battle/demo/BattlepassContext'
import { SeasonProvider } from '@src/contexts/share/SeasonContext'
import { useSecondaryNavBarContext } from '@src/contexts/share/SecondaryNavBarContext'
import theme from '@src/styles/theme'
import type { NextPage } from 'next'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(ApeironCorneredBox)`
  height: calc(100vh - 116px);
  width: 100%;

  ${props => props.theme.breakpoints.up('res1024')} {
    background-image: url(${backgroundJPG.src});
    background-size: cover;
    background-position: center;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    background: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7)),
      url(${backgroundJPG.src});
    background-size: cover;
    background-position: center bottom 60%;
  }

  .Custom-Container {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 50px;
    justify-content: flex-start;
    overflow-y: auto;
  }
  .Custom-Top-Left {
    ${props => props.theme.breakpoints.up('res1024')} {
      aspect-ratio: 0.52;
      max-width: 150px;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      aspect-ratio: 0.88;
      max-width: 230px;
    }
    width: 100%;
  }
  .Custom-Bottom-Left {
    ${props => props.theme.breakpoints.up('res1024')} {
      aspect-ratio: 0.545;
      max-width: 350px;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      aspect-ratio: 0.38;
      max-width: 50px;
    }
    width: 100%;
  }
  .Custom-Top-Right {
    ${props => props.theme.breakpoints.up('res1024')} {
      aspect-ratio: 0.545;
      max-width: 120px;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      aspect-ratio: 0.77;
      max-width: 90px;
    }
    width: 100%;
  }
  .Custom-Bottom-Right {
    ${props => props.theme.breakpoints.up('res1024')} {
      aspect-ratio: 1.755;
      max-width: 1000px;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      aspect-ratio: 0.74;
      max-width: 300px;
    }
    width: 100%;
  }
`

const StyledBattleDemoTitle = styled(ApeironBattleDemoTitle)`
  position: absolute;
  ${props => props.theme.breakpoints.up('res1024')} {
    top: 45px;
    left: 90px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    top: 40px;
    left: 20px;
  }
`

const StyledBattlepassContainer = styled(BattlepassContainer)`
  margin: 150px 0px 60px 0px;
`

const BattlepassPage: NextPage<Props> = () => {
  const [cornerAnimated, setCornerAnimated] = useState<boolean>(false)

  const { setupBar } = useSecondaryNavBarContext()

  const { t } = useTranslation()

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const handleOnAnimationEnd = useCallback(() => {
    setCornerAnimated(true)
  }, [])

  useEffect(() => {
    setupBar(<BattleDemoSecNavBar route={ROUTE.BATTLE_DEMO_BATTLE_PASS} />)

    return () => setupBar(null)
  }, [setupBar])

  return (
    <SeasonProvider>
      <BattlepassProvider>
        <Container
          bottomLeft={mobile ? mobileBottomLeftPNG.src : bottomLeftPNG.src}
          bottomRight={mobile ? mobileBottomRightPNG.src : bottomRightPNG.src}
          topLeft={mobile ? mobileTopLeftPNG.src : topLeftPNG.src}
          topRight={mobile ? mobileTopRightPNG.src : topRightPNG.src}
          onAnimationEnd={handleOnAnimationEnd}
        >
          <StyledBattleDemoTitle title={t('battle_demo.pass.page_title')} />
          <StyledBattlepassContainer show={cornerAnimated} />
        </Container>
      </BattlepassProvider>
    </SeasonProvider>
  )
}

type Props = {}

export default BattlepassPage
